import type { CompileResponse } from "../types/compilation";
import { api } from "./api";

const EXTERNAL_COMPILE_URL = "https://latex.ytotech.com/builds/sync";
const COMPILE_TIMEOUT_MS = 90_000;

let _currentBlobUrl: string | null = null;

function revokePreviousBlob() {
  if (_currentBlobUrl) {
    URL.revokeObjectURL(_currentBlobUrl);
    _currentBlobUrl = null;
  }
}

/**
 * Compile LaTeX source to PDF for demo mode.
 *
 * Strategy:
 * 1. Try the backend /api/v1/compile endpoint (works if backend is reachable).
 * 2. Fall back to latexonline.cc external API (direct browser request).
 * 3. If both fail, return an error response.
 */
export async function demoCompile(source: string): Promise<CompileResponse> {
  revokePreviousBlob();

  // 1) Try the regular backend
  try {
    const result = await api.post<CompileResponse>("/api/v1/compile", {
      source,
      engine: "pdflatex",
    });
    return result;
  } catch {
    // Backend unavailable, fall through to external
  }

  // 2) Try latex.ytotech.com directly from the browser
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), COMPILE_TIMEOUT_MS);

    const response = await fetch(EXTERNAL_COMPILE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        compiler: "pdflatex",
        resources: [{ main: true, content: source }],
      }),
      signal: controller.signal,
    });

    clearTimeout(timeout);

    const contentType = response.headers.get("content-type") || "";
    if (response.ok && contentType.includes("application/pdf")) {
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      _currentBlobUrl = blobUrl;

      return {
        success: true,
        pdf_url: blobUrl,
        errors: [],
        warnings: [],
        compile_time_ms: 0,
        log: null,
      };
    }

    const errorText = await response.text().catch(() => "");
    return {
      success: false,
      pdf_url: null,
      errors: [
        {
          line: null,
          message: errorText || `Compilation returned status ${response.status}`,
          severity: "error",
          file: null,
        },
      ],
      warnings: [],
      compile_time_ms: 0,
      log: errorText || null,
    };
  } catch (err) {
    const isCors =
      err instanceof TypeError && (err.message.includes("Failed to fetch") || err.message.includes("NetworkError"));

    return {
      success: false,
      pdf_url: null,
      errors: [
        {
          line: null,
          message: isCors
            ? "PDF preview unavailable in demo mode (external compiler blocked by CORS). Start the backend to compile LaTeX."
            : `Compilation failed: ${err instanceof Error ? err.message : String(err)}`,
          severity: "error",
          file: null,
        },
      ],
      warnings: [],
      compile_time_ms: 0,
      log: null,
    };
  }
}

/**
 * Clean up any blob URLs created by demoCompile.
 * Call on unmount or when switching templates.
 */
export function cleanupDemoCompile() {
  revokePreviousBlob();
}
